// messagingSafety.ts
// Détection simplifiée des contenus "hors plateforme" : email, social, téléphone, paiement.
// Pas de notion hard/soft : juste des checks catégoriels + un booléen global.

//////////////////////////////
// Normalisation
//////////////////////////////

function isOnlyNumbersMessage(text: string): boolean {
  // On garde seulement chiffres et +
  const stripped = text.replace(/[^\d+]/g, '')

  // Si vide → false
  if (!stripped) return false

  // Si le message original ne contenait QUE chiffres / séparateurs classiques
  const onlyAllowedChars = /^[\d+\s().-]+$/.test(text)

  return onlyAllowedChars
}

/** Replace all fullwidth digits/letters and common obfuscations, remove zero-width, map spelled numbers -> digits, map "at"/"dot" variants to @/. */
export function normalizeText(input: string): string {
  if (!input) return ''

  let s = String(input)

  // 1) Unicode NFD (supprime accents)
  s = s.normalize('NFD').replace(/\p{M}/gu, '')

  // 2) Zero-width
  s = s.replace(/[\u200B-\u200F\uFEFF\u2060]/g, '')

  // 3) Fullwidth digits → ASCII
  s = s.replace(/[\uFF10-\uFF19]/g, (c) => String(c.charCodeAt(0) - 0xff10))

  // 4) Toutes familles de chiffres “math” 0..9 (bold, ds, ss, ss-bold, mono)
  s = s.replace(/[\u{1D7CE}-\u{1D7FF}]/gu, (ch) => {
    const cp = ch.codePointAt(0)!
    const groups = [
      [0x1d7ce, 0x1d7d7],
      [0x1d7d8, 0x1d7e1],
      [0x1d7e2, 0x1d7eb],
      [0x1d7ec, 0x1d7f5],
      [0x1d7f6, 0x1d7ff],
    ]
    for (const [start, end] of groups) {
      if (cp >= start && cp <= end) return String(cp - start)
    }
    return ch
  })

  // 5) Quotes + fullwidth punct
  s = s
    .toLowerCase()
    .replace(/[’`´”“]/g, "'")
    .replace(/＠/g, '@')
    .replace(/．|。/g, '.')
    .replace(/[丨|]/g, '|')

  // 6) Supprimer tags HTML (ex: <br/>)
  s = s.replace(/<[^>]+>/g, ' ')

  // 7) Supprimer les commentaires entre parenthèses UNIQUEMENT s'ils contiennent des lettres
  // 7a) Si le contenu est "technique" (uniquement lettres/chiffres/._-), on DÉPLIE (garde le contenu)
  s = s.replace(/\(([\p{L}\p{N}._-]+)\)/gu, '$1')
  // 7b) Sinon, si ce sont des "commentaires" avec lettres + espaces, on SUPPRIME tout
  s = s.replace(/\((?=[^)]*\p{L})(?=[^)]*\s)[^)]*\)/gu, ' ')

  // 7c) HTML entities & percent-encoding pour @ et .
  s = s
    // @
    .replace(/&#(?:0*64|x40);?|&commat;/gi, '@')
    .replace(/%40/gi, '@')
    // .
    .replace(/&#(?:0*46|x2e);?/gi, '.')
    .replace(/%2e/gi, '.')

  // 8) Mots at/dot → symboles
  s = s.replace(/\b(?:\(|\[)?\s*(?:at|ats|arobase|arrobase|arroba)\s*(?:\)|\])?\b/gi, '@')
  s = s.replace(/\b(?:\(|\[)?\s*(?:dot|point|\.|pointcom|pointfr|pointnet)\s*(?:\)|\])?\b/gi, '.')

  // 9) Recolle espaces autour de @ et .
  s = s.replace(/\s*@\s*/g, '@').replace(/\s*\.\s*/g, '.')

  // 10) Séparateurs visuels courants -> espace
  s = s.replace(/[ \u00A0\u2000-\u200A\u202F\u205F\u3000~·•|/\\\u2024\u2026*()]+/g, ' ')

  // 11) Nombres FR/EN/ES unités simples
  const numWordMap: Record<string, string> = {
    zero: '0',
    zéro: '0',
    oh: '0',
    o: '0',
    un: '1',
    one: '1',
    uno: '1',
    deux: '2',
    two: '2',
    dos: '2',
    trois: '3',
    three: '3',
    tres: '3',
    quatre: '4',
    four: '4',
    cuatro: '4',
    cinq: '5',
    five: '5',
    cinco: '5',
    six: '6',
    six_en: '6',
    seis: '6',
    sept: '7',
    seven: '7',
    siete: '7',
    huit: '8',
    eight: '8',
    ocho: '8',
    neuf: '9',
    nine: '9',
    nueve: '9',
  }
  s = s.replace(
    /\b(zero|zéro|oh|o|un|deux|trois|quatre|cinq|six|sept|huit|neuf|one|two|three|four|five|seven|eight|nine|cero|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve)\b/giu,
    (m) => (m.toLowerCase() === 'six' ? '6' : numWordMap[m] ?? numWordMap[m.toLowerCase()] ?? m),
  )

  // 12) FR nombres composés (10..99)
  s = replaceFrenchNumberWords(s)

  // 13) Quotes locales d’email
  s = s.replace(/"([^"]+)"\s*@/g, '$1@')

  // 14) Look-alikes proches des chiffres (global) + quelques leet courants
  const look = (c: string) => ({ o: '0', i: '1', l: '1', O: '0', I: '1', L: '1' }[c] ?? c)
  s = s.replace(/(\d)([oilOIL])/g, (_, d, c) => d + look(c))
  s = s.replace(/([oilOIL])(\d)/g, (_, c, d) => look(c) + d)

  // 15) Collapse espaces
  s = s.replace(/\s+/g, ' ').trim()

  return s
}

/** Remplace dans la string des nombres FR composés par leurs chiffres (10..99) */
function replaceFrenchNumberWords(input: string): string {
  let s = input

  // 10..19 (y compris composés)
  const teens: Record<string, number> = {
    dix: 10,
    onze: 11,
    douze: 12,
    treize: 13,
    quatorze: 14,
    quinze: 15,
    seize: 16,
    'dix-sept': 17,
    'dix sept': 17,
    'dix-huit': 18,
    'dix huit': 18,
    'dix-neuf': 19,
    'dix neuf': 19,
  }
  s = s.replace(/\b(dix(?:[-\s](?:sept|huit|neuf))?|onze|douze|treize|quatorze|quinze|seize)\b/giu, (m) =>
    String(teens[m.toLowerCase()]),
  )

  // Dizaines 20..60 + unités optionnelles
  const tens: Record<string, number> = { vingt: 20, trente: 30, quarante: 40, cinquante: 50, soixante: 60 }
  const units: Record<string, number> = {
    un: 1,
    deux: 2,
    trois: 3,
    quatre: 4,
    cinq: 5,
    six: 6,
    sept: 7,
    huit: 8,
    neuf: 9,
  }
  s = s.replace(
    new RegExp(`\\b(${Object.keys(tens).join('|')})(?:[-\\s]+(?:et\\s+)?(${Object.keys(units).join('|')}))?\\b`, 'giu'),
    (_, t: string, u?: string) => String(tens[t.toLowerCase()] + (u ? units[u.toLowerCase()] : 0)),
  )

  // 70..79 : soixante + (10..19)
  s = s.replace(
    /\bsoixante[-\s]+(dix(?:[-\s](?:sept|huit|neuf))?|onze|douze|treize|quatorze|quinze|seize)\b/giu,
    (_, teen: string) =>
      String(
        60 +
          (
            {
              dix: 10,
              onze: 11,
              douze: 12,
              treize: 13,
              quatorze: 14,
              quinze: 15,
              seize: 16,
              'dix-sept': 17,
              'dix sept': 17,
              'dix-huit': 18,
              'dix huit': 18,
              'dix-neuf': 19,
              'dix neuf': 19,
            } as any
          )[teen.toLowerCase()],
      ),
  )

  // 80..99 : quatre-vingt(s) + (unités ou 10..19)
  s = s.replace(/\bquatre[-\s]?vingts?\b/giu, '80')
  s = s.replace(
    new RegExp(`\\bquatre[-\\s]?vingts?[-\\s]+(?:et\\s+)?(${Object.keys(units).join('|')})\\b`, 'giu'),
    (_, u: string) => String(80 + units[u.toLowerCase()]),
  )
  s = s.replace(
    /\bquatre[-\s]?vingts?[-\s]+(dix(?:[-\s](?:sept|huit|neuf))?|onze|douze|treize|quatorze|quinze|seize)\b/giu,
    (_, teen: string) =>
      String(
        80 +
          (
            {
              dix: 10,
              onze: 11,
              douze: 12,
              treize: 13,
              quatorze: 14,
              quinze: 15,
              seize: 16,
              'dix-sept': 17,
              'dix sept': 17,
              'dix-huit': 18,
              'dix huit': 18,
              'dix-neuf': 19,
              'dix neuf': 19,
            } as any
          )[teen.toLowerCase()],
      ),
  )

  return s
}

//////////////////////////////
// Helpers pour détection
//////////////////////////////

function extractDigitBlocks(text: string): string[] {
  // Autorise jusqu’à 10 non-digits entre deux digits et tolère 1 non-digit avant la première
  const matches = Array.from(text.matchAll(/(?:^|[^\d])(\+?\d(?:\D{0,10}\d){3,})/g)).map((m) => m[1])
  if (!matches.length) return []

  const lookalikes: Record<string, string> = {
    o: '0',
    O: '0',
    i: '1',
    I: '1',
    l: '1',
    z: '2',
    Z: '2',
    s: '5',
    S: '5',
    b: '8',
    B: '8',
    '!': '1',
  }

  return matches.map((m) => {
    const plus = m.trim().startsWith('+') ? '+' : ''
    const m2 = m.replace(/[oOilIzZsSbB!]/g, (c) => lookalikes[c] ?? c)
    const digits = m2.replace(/[^\d]/g, '')
    return plus + digits
  })
}

/** Recompose un probable email après normalisation (on a déjà converti at/dot) */
function findLikelyEmails(text: string): string[] {
  const stitched = text
    .replace(/\s*@\s*/g, '@')
    .replace(/\s*\.\s*/g, '.')
    .replace(/＠/g, '@')
    .replace(/[．。]/g, '.')

  const EMAIL_RE = /\b(?:"[^"]+"|[^\s"@]+)@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,24}\b/gu
  return stitched.match(EMAIL_RE) ?? []
}

//////////////////////////////
// Motifs simples (sociaux / email / paiement)
//////////////////////////////

// Réseaux sociaux / mots-clefs (tolère obfuscations simples, ex: i.n.s.t.a.g.r.a.m)
const SEP = `[\\s._\\-]*`
const OBF = (w: string) =>
  w
    .split('')
    .map((ch) => `${ch}${SEP}`)
    .join('')
const SOCIAL_RE = new RegExp(
  `\\b(?:instagram|${OBF('instagram')}|facebook|${OBF('facebook')}|tiktok|${OBF('tiktok')}|snap(?:chat)?|${OBF(
    'snapchat',
  )}|telegram|${OBF('telegram')}|whatsapp|x|twitter|x${SEP}com|linkedin|${OBF('linkedin')})\\b`,
  'iu',
)

// WhatsApp (obfuscations très agressives)
const WHATSAPP_RE = new RegExp(
  String.raw`(?:w(?=.{0,12}p)[\s\S]?h[\s\S]?a[\s\S]?t[\s\S]?s[\s\S]?a[\s\S]?p[\s\S]?p|h(?=.{0,12}p)[\s\S]?a[\s\S]?t[\s\S]?s[\s\S]?a[\s\S]?p[\s\S]?p|w(?=.{0,12}p)[\s\S]?a[\s\S]?t[\s\S]?s[\s\S]?a[\s\S]?p[\s\S]?p|w(?=.{0,12}p)[\sS]?h[\s\S]?t[\s\S]?s[\s\S]?a[\s\S]?p[\s\S]?p|w(?=.{0,12}p)[\s\S]?h[\s\S]?a[\s\S]?s[\s\S]?a[\s\S]?p[\s\S]?p|w(?=.{0,12}p)[\s\S]?h[\s\S]?a[\s\S]?t[\s\S]?a[\s\S]?p[\s\S]?p|w(?=.{0,12}p)[\s\S]?h[\s\S]?a[\s\S]?t[\s\S]?s[\s\S]?p[\s\S]?p|w(?=.{0,12}p)[\s\S]?h[\s\S]?a[\s\S]?t[\s\S]?s[\s\S]?a[\s\S]*?p)`,
  'iu',
)

// Email — politique simple (3 règles)
const EMAIL_AT_RE = /@/u
const EMAIL_PROVIDER_RE =
  /\b(?:gmail|yahoo|hotmail|outlook|protonmail|laposte|icloud|orange|wanadoo|free|live|gmx|aol)\b/iu
const EMAIL_TLD_TOKEN_RE = /(?<![a-z])(?:com|fr)(?![a-z])/iu

// Paiement (incl. cash + apps courantes)
const PAYMENT_RE = new RegExp(
  [
    '\\b(?:virement|rib|iban|paiement|payer|especes|esp[èe]ces|cash|liquide|facture|acompte|versement)\\b',
    'paypal',
    'wero',
    'lydia',
    'sumeria',
    'paylib',
    'lyf(?:\\s*pay)?',
    'revolut',
    'fin\\.?do',
    'splitwise',
    'tricount',
    "sans\\s+passer\\s+par\\s+l['’]?(?:appli|application)",
    '\\bpourcentage\\b',
  ].join('|'),
  'iu',
)

// Téléphone (France + heuristiques)
const PHONE_FR_INLINE_RE = /(?:^|[^\d])(?:(?:\+|00)\s?33|0)\s*[1-9](?:[\s().-]*\d){8}/u

// 7+ chiffres consécutifs → signal "phone"
const PHONE_7_CONSEC_RE = /\b\d{7,}\b/u

// Intention d'appel (compte comme "phone") – couvre s’appeler/appeler + téléphoner (présent, futur, conditionnel, impératif)
// const CALL_INTENT_RE =
//   /\b(?:s['’ ]*)?appel(?:er|e(?:s|z|nt)?|ons|ez|lent|l(?:erai|erais|erait|eront|erons|erez)|er(?:ai|ais|a|ont|ons|ez))\b|\b(?:t[ée]l[ée]?phon(?:er|e(?:s|z)?|ons|era(?:i|is)?|erai|erais|erait|eront|erons|erez))\b/iu

//////////////////////////////
// API détection simplifiée
//////////////////////////////

export type Detection = {
  email: boolean
  social: boolean
  phone: boolean
  payment: boolean
  reasons: string[] // messages courts pour debug / analytics
}

/** Détection unique (pas de hard/soft) */
export function analyzeOffPlatform(textInput: string | null | undefined): Detection {
  const reasons: string[] = []
  if (!textInput) return { email: false, social: false, phone: false, payment: false, reasons }

  const text = normalizeText(textInput)

  // --- Téléphone
  let phone = false

  if (isOnlyNumbersMessage(text)) {
    phone = true
    reasons.push('phone:only_numbers')
  } else if (PHONE_FR_INLINE_RE.test(text)) {
    phone = true
    reasons.push('phone:fr_inline')
  } else {
    const blocks = extractDigitBlocks(text)
    for (const block of blocks) {
      const digitsOnly = block.startsWith('+') ? block.slice(1) : block
      if (PHONE_7_CONSEC_RE.test(text)) {
        phone = true
        reasons.push('phone:7_consecutive')
      } else if (/^0[1-9]\d{8}$/.test(digitsOnly)) {
        phone = true
        reasons.push('phone:fr_10')
        break
      }
      if (/^33[1-9]\d{8}$/.test(digitsOnly)) {
        phone = true
        reasons.push('phone:fr_33')
        break
      }
      if (/^[67]\d{8}$/.test(digitsOnly)) {
        phone = true
        reasons.push('phone:fr_mobile_missing0')
        break
      }
      if (digitsOnly.length >= 10 && /[0-9]{10,}/.test(digitsOnly)) {
        phone = true
        reasons.push('phone:generic_long')
        break
      }
      // intention d'appel → on considère comme signal "phone"
      // if (CALL_INTENT_RE.test(text)) {
      //   phone = true
      //   reasons.push('phone:call_intent')
      // }
    }
  }

  // --- Email
  let email = false
  // 1) @ présent (reconstruction d'emails si possible)
  if (EMAIL_AT_RE.test(text)) {
    const emails = findLikelyEmails(text)
    if (emails.length > 0) {
      email = true
      reasons.push('email:addr')
    } else {
      // @ présent mais pas RFC-like → on considère quand même "email"
      email = true
      reasons.push('email:at_present')
    }
  }
  // 2) Provider connu
  if (EMAIL_PROVIDER_RE.test(text)) {
    email = true
    reasons.push('email:provider')
  }
  // 3) TLD en token (séparé par non-lettres)
  if (EMAIL_TLD_TOKEN_RE.test(text)) {
    email = true
    reasons.push('email:tld_token')
  }

  // --- Social
  const maybeWhatsapp = text.includes('p') && (text.includes('w') || text.includes('h'))
  const social = SOCIAL_RE.test(text) || (maybeWhatsapp && WHATSAPP_RE.test(text))
  if (WHATSAPP_RE.test(text)) reasons.push('social:whatsapp_obfuscated')

  // --- Paiement
  const payment = PAYMENT_RE.test(text)
  if (payment) reasons.push('payment:keyword')

  return { email, social, phone, payment, reasons }
}

/** Helper global */
export function hasOffPlatformSignal(text: string | null | undefined): boolean {
  const d = analyzeOffPlatform(text)
  return d.email || d.social || d.phone || d.payment
}

//////////////////////////////
// Statuts restreints (utile ailleurs)
//////////////////////////////

export function isRestrictedStatus(raw?: string): boolean {
  if (!raw) return false
  const s = String(raw).toUpperCase()
  return (
    s === 'PENDING' ||
    s === 'DRAFT' ||
    s === 'ACCEPTED' ||
    s === 'APPROVED' ||
    s === 'REFUSED' ||
    s === 'REJECTED' ||
    s === 'CANCELLED'
  )
}
